import React from "react";
import ServiceFactory from "../../service";

/*
 * Email Automation shared bits (AD_CAMPAIGNS_EMAIL_AUTOMATION_UX.md §4).
 * API: /v1/campaign/email/* on CampaignService. The model is deliberately
 * simple — single trigger → delay → single email; there are no journeys
 * or branching, so there is NO flow canvas anywhere in this section.
 *
 * Compliance facts the UI must state (spec §4):
 *  - each contact gets an automation ONCE EVER (dedup is permanent)
 *  - contacts with no opt-in record are treated as opted in
 *  - the CAN-SPAM footer is NOT auto-injected — the email body must
 *    include it
 */

export function emailSvc(api) {
  const Service = ServiceFactory.getType(`/campaign/email${api}`, "product"); // /v1 root
  return new Service();
}

// Starter automations are re-seeded per tenant in Draft. Cart Recovery
// still needs honest labeling (viewed-product population). WelcomeSeries
// was un-stubbed 2026-08-31 (§288.1): the new-contact trigger IS wired
// end-to-end in the worker (new contacts w/ a primary email, once-ever
// dedupe) — the old "coming soon" banner was wrong and blocked Activate.
export const STARTER_NOTES = {
  CartAbandonment: {
    comingSoon: false,
    note: "Today this reaches the same visitors as Products Viewed (viewed a product, didn't convert): true cart events aren't wired in yet."
  }
};
export function starterNote(a) {
  return a && STARTER_NOTES[a.type] || null;
}
export function delayLabel(sc) {
  if (!sc) return "—";
  if (sc.sendImmediately) return "immediately";
  const parts = [];
  if (sc.delayDays) parts.push(`${sc.delayDays}d`);
  if (sc.delayHours) parts.push(`${sc.delayHours}h`);
  if (sc.delayMinutes) parts.push(`${sc.delayMinutes}m`);
  return parts.length ? `${parts.join(" ")} after trigger` : "immediately";
}
export function statusChipStyle(status) {
  if (status === "Running") return {
    background: "var(--plk-success-soft)",
    color: "var(--plk-success-text)"
  };
  if (status === "Paused") return {
    background: "var(--plk-warning-soft)",
    color: "var(--plk-warning-text)"
  };
  return {
    background: "var(--plk-surface-alt)",
    color: "var(--plk-text-dim)"
  }; // Draft
}
export function triggerLabel(a) {
  const tc = a && a.triggerConfig;
  if (a && a.type === "CustomTrigger" && tc && tc.eventName && a.triggerType !== "ProductViewed") {
    return `${tc.eventName} (${a.triggerType || "event"})`;
  }
  const MAP = {
    CartAbandoned: "Cart abandoned",
    ProductViewed: "Products viewed",
    WelcomeSeries: "New contact",
    CustomerLoyalty: "Repeat customer",
    Conversion: "Conversion event",
    Engagement: "Engagement event"
  };
  return MAP[a && a.triggerType] || a && a.triggerType || "—";
}

// Recipient funnel: entered = all rows; waiting = Pending (delay window);
// sent = everything at or past Sent.
export function recipientFunnel(recipients) {
  const rows = recipients || [];
  const waiting = rows.filter(r => r.status === "Pending").length;
  const sent = rows.filter(r => ["Sent", "Delivered", "Opened", "Clicked"].includes(r.status)).length;
  const bounced = rows.filter(r => ["Bounced", "Unsubscribed"].includes(r.status)).length;
  return {
    entered: rows.length,
    waiting,
    sent,
    bounced
  };
}
export function num(v) {
  return (Number(v) || 0).toLocaleString("en-US");
}

// Automations | Journeys switcher under the "Email" nav entry. Journeys
// (EMAIL_JOURNEYS.md) are the multi-step superset; the single-email rail
// stays as is.
export function SectionTabs({
  active,
  history
}) {
  // Inline beside the page heading (same row as the primary button), the way
  // the Reports header lays out its Reports | Schedules | Sent toggles.
  const tabs = [["journeys", "Journeys", "/Email/journeys"]];
  return /*#__PURE__*/React.createElement("div", {
    style: {
      display: "inline-flex",
      gap: 2
    }
  }, tabs.map(([k, l, path]) => /*#__PURE__*/React.createElement("button", {
    key: k,
    className: "territory-toggle" + (active === k ? " active" : ""),
    onClick: () => active !== k && history.push(path)
  }, l)));
}